import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { CardScore } from '@sorare/core/src/components/collections/CardScore';
import { DetailsDialogBanner } from '@sorare/core/src/components/collections/DetailsDialogBanner';
import { Warning } from '@sorare/core/src/components/collections/Warning';
import Dialog from '@sorare/core/src/components/dialog';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useTokenOfferBelongsToUser } from '@sorare/core/src/hooks/useTokenOfferBelongsToUser';
import { isMyCardListedOnMarket } from '@sorare/core/src/lib/cards';
import { getCollectionsTeamShield } from '@sorare/core/src/lib/collections';
import { isLightColor } from '@sorare/core/src/lib/colors';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import useCancelOffer from '@sorare/marketplace/src/hooks/offers/useCancelOffer';

import CollectionBackground from 'components/collections/CollectionBackground';
import { CollectionBonuses } from 'components/collections/CollectionBonuses';

import {
  CardPreview_cardCollection,
  CardPreview_cardCollectionCard,
} from './__generated__/index.graphql';

const DialogContainer = styled(CollectionBackground)`
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--double-unit);
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    width: 480px;
  }
`;
const ActionButtonsWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;
const CardImageWrapper = styled.div`
  padding: var(--quadruple-unit);
  max-width: 250px;
`;
const Root = styled(Vertical).attrs({ center: true, gap: 3 })`
  position: relative;
  isolation: isolate;
  padding: calc(6 * var(--unit)) var(--double-unit) var(--triple-unit);
  background: linear-gradient(180deg, var(--c-nl-200), var(--c-nl-200)),
    linear-gradient(180deg, var(--background-color) 0%, var(--c-nl-800) 100%);
`;
const CardWrapper = styled.div`
  padding: 0 var(--quadruple-unit);
  max-width: 250px;
`;

type Props = {
  cardCollectionCard: CardPreview_cardCollectionCard;
  bannerPictureUrl: string;
  open?: boolean;
  onClose: () => void;
  cardCollection?: CardPreview_cardCollection;
};
const CardPreview = ({
  cardCollectionCard,
  bannerPictureUrl,
  open,
  onClose,
  cardCollection,
}: Props) => {
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();
  const [unlisting, setUnlisting] = useState(false);
  const [unlisted, setUnlisted] = useState(false);
  const { anyCard: card, scoreBreakdown } = cardCollectionCard;
  const { __typename, total, ...scores } = scoreBreakdown;
  const theoricalScore = Object.values(scores).reduce(
    (sum, value) => sum + value
  );
  const cancelOffer = useCancelOffer();
  const belongsToUser = useTokenOfferBelongsToUser();

  const cardListed = isMyCardListedOnMarket(card);

  const onUnlist = async () => {
    const offer = card.myMintedSingleSaleOffer;
    if (offer && belongsToUser(offer) && offer.blockchainId) {
      setUnlisting(true);
      const errors = await cancelOffer(offer.blockchainId);
      if ((errors?.length || 0) > 0) {
        setUnlisted(false);
      } else {
        setUnlisted(true);
      }
      setUnlisting(false);
    }
  };

  const displayWarning = cardListed && !unlisted;

  if (enableMyClubSplit) {
    const { styleGuideColor } = cardCollection?.team || {};

    const backgroundColor =
      styleGuideColor && !isLightColor(styleGuideColor)
        ? styleGuideColor
        : 'var(--c-nl-700-opaque)';

    return (
      <Dialog open={open} maxWidth="xs" onClose={onClose} hideHeader fullWidth>
        {({ CloseButton }) => (
          <Root
            style={{ '--background-color': backgroundColor } as CSSProperties}
          >
            <ActionButtonsWrapper>
              <CloseButton onClose={onClose} />
            </ActionButtonsWrapper>
            <Vertical gap={1.5} center>
              <CardWrapper>
                <Card card={card} width={320} />
              </CardWrapper>
              <CardScore
                score={unlisted ? theoricalScore : total}
                listed={displayWarning}
              />
            </Vertical>
            <CollectionBonuses
              cardCollectionCard={cardCollectionCard}
              displayWarning={displayWarning}
            />
            {displayWarning && (
              <Warning onClick={onUnlist} loading={unlisting} />
            )}
          </Root>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} maxWidth="sm" onClose={onClose} hideHeader>
      {({ CloseButton }) => (
        <DialogContainer colored bannerPictureUrl={bannerPictureUrl}>
          <ActionButtonsWrapper>
            <CloseButton onClose={onClose} />
          </ActionButtonsWrapper>
          <CardImageWrapper>
            <Card card={card} width={320} />
          </CardImageWrapper>
          <CardScore
            score={unlisted ? theoricalScore : total}
            listed={displayWarning}
          />
          <CollectionBonuses
            cardCollectionCard={cardCollectionCard}
            displayWarning={displayWarning}
          />
          {displayWarning && <Warning onClick={onUnlist} loading={unlisting} />}
          <DetailsDialogBanner
            teamShield={getCollectionsTeamShield(cardCollection)}
            showShieldInfo
            showWarning
          />
        </DialogContainer>
      )}
    </Dialog>
  );
};

CardPreview.fragments = {
  cardCollectionCard: gql`
    fragment CardPreview_cardCollectionCard on CardCollectionCard {
      id
      anyCard {
        slug
        pictureUrl
        myMintedSingleSaleOffer {
          id
          blockchainId
          ...useTokenOfferBelongsToUser_offer
        }
        ...isMyCardListedOnMarket_anyCard
        ...Card_anyCard
      }
      scoreBreakdown {
        firstOwner
        firstSerialNumber
        holding
        owner
        shirtMatchingSerialNumber
        specialEdition
        total
      }
      ...CollectionBonuses_cardCollectionCard
    }
    ${isMyCardListedOnMarket.fragments.anyCard}
    ${useTokenOfferBelongsToUser.fragments.offer}
    ${CollectionBonuses.fragments.cardCollectionCard}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<CardPreview_cardCollectionCard>,
  cardCollection: gql`
    fragment CardPreview_cardCollection on CardCollection {
      slug
      team {
        ... on TeamInterface {
          slug
          styleGuideColor
        }
      }
      ...getCollectionsShield_cardCollection
    }
    ${getCollectionsTeamShield.fragments.cardCollection}
  ` as TypedDocumentNode<CardPreview_cardCollection>,
};

export default CardPreview;
